import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref } from "vue";

interface IndicatorBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const useSlidingIndicator = (
  rootRef: Ref<HTMLElement | null>,
  activeKey: () => unknown,
  activeSelector = ".is-active",
) => {
  const box = ref<IndicatorBox | null>(null);

  const isAnimated = ref(false);

  const measure = (): void => {
    const activeItem = rootRef.value?.querySelector<HTMLElement>(activeSelector);

    if (!activeItem) {
      box.value = null;

      return;
    }

    box.value = {
      left: activeItem.offsetLeft,
      top: activeItem.offsetTop,
      width: activeItem.offsetWidth,
      height: activeItem.offsetHeight,
    };
  };

  let resizeObserver: ResizeObserver | null = null;

  onMounted(async () => {
    measure();

    if (typeof ResizeObserver !== "undefined" && rootRef.value) {
      resizeObserver = new ResizeObserver(() => measure());

      resizeObserver.observe(rootRef.value);
    }

    await document.fonts?.ready;

    measure();

    requestAnimationFrame(() => {
      isAnimated.value = true;
    });
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
  });

  watch(activeKey, async () => {
    await nextTick();

    measure();
  });

  const indicatorStyle = computed(() => {
    if (!box.value) {
      return { display: "none" };
    }

    return {
      width: `${box.value.width}px`,
      height: `${box.value.height}px`,
      transform: `translate(${box.value.left}px, ${box.value.top}px)`,
    };
  });

  const hasIndicator = computed(() => box.value !== null);

  return { indicatorStyle, hasIndicator, isAnimated };
};
